"use client";

import AddToFav from "@/modules/add-to-fav/add-to-fav";
import CreateWishlistButton from "@/modules/add-to-fav/create-wishlist-button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import ModalTemplate from "@/components/molecules/modal-template";
import { Locales } from "@/lib/i18n";
import { __, getPermalink } from "@/lib/i18n/functions";
import Link from "next/link";
import { useWishlist } from "@/hooks/useWishlist";

type WishlistSidebarProps = {
  locale: Locales;
};

const WishlistSidebar = ({ locale }: WishlistSidebarProps) => {
  const { lists, activeListName, setActiveList } = useWishlist();

  return (
    <aside className="min-w-[280px] flex-1 space-y-5 pt-4">
      <Link
        href={getPermalink(locale, "account")}
        className="text-primary-light flex cursor-pointer items-center gap-2.5 font-medium"
      >
        <ChevronLeft size={24} />
        {__("Back to my account", locale)}
      </Link>

      <h2 className="leading-h1 font-cabinet-grotesk text-3xl font-medium max-lg:hidden">
        {__("My favourites lists", locale)}
      </h2>
      <h2 className="leading-h1 font-cabinet-grotesk text-lg font-medium min-lg:hidden">Listes d’envie</h2>

      <ul className="border-primary-light/50 space-y-2.5 border-b pb-5">
        {lists?.length === 0 && <li>{__("No list created.", locale)}</li>}
        {lists?.map((list, index) => {
          if (!list?.listName?.trim()) return null;

          return (
            <li key={index}>
              <div
                onClick={() => setActiveList(list.listName)}
                className={cn(
                  "hover:bg-pink-primary group flex cursor-pointer items-center justify-between rounded-md px-3.5 py-3 transition-all duration-300 ease-in-out hover:text-white",
                  list.listName === activeListName && "bg-pink-primary text-white"
                )}
              >
                <p className="font-medium">
                  {list.listName}{" "}
                  <span
                    className={cn(
                      "text-primary-light transition-all duration-300 ease-in-out group-hover:text-white",
                      list?.listName === activeListName && "text-white"
                    )}
                  >
                    ({list?.products?.length})
                  </span>
                  <br />
                  <span
                    className={cn(
                      "text-primary-light block text-xs transition-all duration-300 ease-in-out group-hover:text-white lg:hidden",
                      list?.listName === activeListName && "bg-pink-primary text-white"
                    )}
                  >
                    {`${list?.date?.from} - ${list?.date?.to}`}
                  </span>
                </p>
                <ChevronRight size={24} />
              </div>
            </li>
          );
        })}
      </ul>

      <ModalTemplate
        title={__("Please select your list", locale)}
        className="w-full sm:max-w-[506px]"
        classNameTitle="text-lg font-normal"
        triggerAsChild
        modalTrigger={<CreateWishlistButton locale={locale} />}
      >
        <AddToFav locale={locale} showForm />
      </ModalTemplate>
    </aside>
  );
};

export default WishlistSidebar;
